import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  defer,
  firstValueFrom,
  forkJoin,
  from,
  of,
  timer,
} from 'rxjs';
import { catchError, map, retry, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { ApiResponse } from '../models/api-response.model';
import { InAppNotification } from '../models/notification.model';

/** Server-to-client hub method, see NotificationHub.ReceiveNotificationEvent. */
const RECEIVE_NOTIFICATION = 'ReceiveNotification';

/** Notifications fetched once after login for the dropdown list. */
const INITIAL_PAGE_SIZE = 20;

/** Upper bound on the in-memory list so a long-lived session cannot grow unbounded. */
const MAX_LIST_SIZE = 50;

/** Reconnect / connect-retry delay: 1s, 2s, 4s ... capped at 30s. */
const backoffMs = (attempt: number): number => Math.min(30_000, 1_000 * 2 ** attempt);

/**
 * In-app notifications.
 *
 * Flow:  login -> load existing notifications ONCE over REST -> connect to the SignalR hub
 *        -> the server pushes new notifications as they are created. There is no polling.
 *
 * REST stays the way to read the saved list and to mark notifications read; SignalR is only
 * the delivery channel for new ones. The database is the source of truth, so after a dropped
 * connection is restored the list is re-synced once to pick up anything missed meanwhile.
 *
 * At most one hub connection exists. initialize() is idempotent per user (the header calls it
 * on every token refresh) and disconnect() tears everything down on logout.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly restUrl = `${environment.apiUrl}/Notifications`;
  /**
   * SignalR hub URL. Prefer an explicit environment.hubUrl when set (required when
   * the API is hosted as an IIS child app under a path like /api, where the hub is
   * at https://host/api/hubs/notifications — NOT the site root). When hubUrl is not
   * configured, fall back to deriving it from apiUrl by stripping the /api/vN suffix
   * (correct when the API is served at the host root, e.g. local dev).
   */
  private readonly hubUrl =
    (environment as { hubUrl?: string }).hubUrl?.trim()
    || `${environment.apiUrl.replace(/\/api\/v\d+\/?$/i, '')}/hubs/notifications`;

  private readonly notificationsSubject = new BehaviorSubject<InAppNotification[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly incomingSubject = new Subject<InAppNotification>();

  /** Notifications shown in the bell dropdown (newest first). */
  readonly notifications$ = this.notificationsSubject.asObservable();
  /** Unread count for the bell badge. */
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  /** True while the initial / re-sync load is in flight. */
  readonly loading$ = this.loadingSubject.asObservable();
  /** Emits each notification that arrives in real time (e.g. to show a toast). */
  readonly incoming$ = this.incomingSubject.asObservable();

  private hub?: HubConnection;
  private activeUser: string | null = null;
  private starting = false;
  /** Emits on disconnect() to cancel any in-flight load / connect attempts. */
  private readonly stop$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly zone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Start notifications for a logged-in user. Safe to call repeatedly: for the same user it
   * does not reload or open a second connection, it only revives a connection that has died
   * (for example after the access token expired while offline).
   */
  initialize(username: string): void {
    if (this.activeUser === username) {
      this.reviveIfClosed();
      return;
    }

    if (this.activeUser) this.disconnect(); // a different user is now logged in
    this.activeUser = username;

    // 1. Load existing notifications once.  2. Then open the real-time connection.
    this.load().subscribe(() => {
      if (this.activeUser === username) this.connect();
    });
  }

  /** Stop the connection and clear all state. Call on logout. */
  disconnect(): void {
    this.activeUser = null;
    this.stop$.next();

    const hub = this.hub;
    this.hub = undefined;
    this.starting = false;
    if (hub) {
      hub.off(RECEIVE_NOTIFICATION);
      hub.stop().catch(() => { /* already stopped */ });
    }

    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.loadingSubject.next(false);
  }

  // ── REST (initial load + user actions) ────────────────────────────────────

  /**
   * Mark one notification read. The badge only drops when the server actually changed the
   * row, so marking an already-read notification cannot push the count out of sync.
   */
  markAsRead(notificationId: number): Observable<ApiResponse<boolean>> {
    return this.http
      .put<ApiResponse<boolean>>(`${this.restUrl}/${notificationId}/read`, {})
      .pipe(
        tap(res => {
          if (!res.success) return;

          this.notificationsSubject.next(
            this.notificationsSubject.value.map(n =>
              n.notificationId === notificationId ? { ...n, isRead: true } : n),
          );
          if (res.data) {
            this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
          }
        }),
      );
  }

  /** Load list + unread count from the server and replace local state. */
  private load(): Observable<void> {
    this.loadingSubject.next(true);

    return forkJoin({
      list: this.http
        .get<ApiResponse<InAppNotification[]>>(this.restUrl, {
          params: { pageNumber: 1, pageSize: INITIAL_PAGE_SIZE },
        })
        .pipe(
          map(res => (res.success ? res.data ?? [] : [])),
          catchError(() => of<InAppNotification[] | null>(null)),
        ),
      count: this.http
        .get<ApiResponse<number>>(`${this.restUrl}/unread-count`)
        .pipe(
          map(res => (res.success ? res.data ?? 0 : 0)),
          catchError(() => of<number | null>(null)),
        ),
    }).pipe(
      takeUntil(this.stop$),
      tap(({ list, count }) => {
        // A failed call leaves the previous value in place rather than wiping the UI.
        if (list) this.notificationsSubject.next(list);
        if (count !== null) this.unreadCountSubject.next(count);
        this.loadingSubject.next(false);
      }),
      map(() => undefined),
    );
  }

  // ── SignalR ───────────────────────────────────────────────────────────────

  private connect(): void {
    if (this.hub) return; // never more than one connection

    const hub = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.getFreshToken(),
        // Auth is the JWT (query string / bearer), not cookies, so credentialed CORS is not needed.
        withCredentials: false,
      })
      // Keep retrying forever with capped backoff; the default policy gives up after ~40s.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ctx => backoffMs(ctx.previousRetryCount),
      })
      .configureLogging(environment.production ? LogLevel.Error : LogLevel.Warning)
      .build();

    // SignalR callbacks must update Angular state inside the zone so the UI refreshes at once.
    hub.on(RECEIVE_NOTIFICATION, (n: InAppNotification) =>
      this.zone.run(() => this.onReceive(n)));

    // Anything created while the connection was down was only saved, not pushed: re-sync once.
    hub.onreconnected(() =>
      this.zone.run(() => this.load().subscribe()));

    this.hub = hub;
    this.start(hub);
  }

  /**
   * Open the connection, retrying with backoff if the first attempt fails (automatic reconnect
   * only covers connections that were already established).
   */
  private start(hub: HubConnection): void {
    if (this.starting) return;
    this.starting = true;
    let failedBefore = false;

    defer(() => (this.hub === hub ? from(hub.start()) : EMPTY))
      .pipe(
        retry({
          delay: (_err, attempt) => {
            failedBefore = true;
            return timer(backoffMs(attempt - 1));
          },
        }),
        takeUntil(this.stop$),
      )
      .subscribe({
        next: () => {
          // If we only got connected after failures, catch up on what we missed meanwhile.
          if (failedBefore) this.zone.run(() => this.load().subscribe());
        },
        complete: () => { this.starting = false; },
        error: () => { this.starting = false; },
      });
  }

  /** Restart a connection that ended (e.g. closed after the token expired). */
  private reviveIfClosed(): void {
    const hub = this.hub;
    if (hub && !this.starting && hub.state === HubConnectionState.Disconnected) {
      this.start(hub);
    }
  }

  /** Current JWT; refreshed first if it has expired (reconnects can outlive the token). */
  private async getFreshToken(): Promise<string> {
    if (!this.authService.isAuthenticated()) {
      try {
        await firstValueFrom(this.authService.refreshAccessToken());
      } catch {
        /* fall through: the server will reject and the normal auth flow takes over */
      }
    }
    return this.authService.getToken() ?? '';
  }

  private onReceive(n: InAppNotification): void {
    const current = this.notificationsSubject.value;
    if (current.some(x => x.notificationId === n.notificationId)) return; // already have it

    this.notificationsSubject.next([n, ...current].slice(0, MAX_LIST_SIZE));
    if (!n.isRead) {
      this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
    }
    this.incomingSubject.next(n);
  }
}
